import kebabCase from 'lodash.kebabcase';
import { isNil, isString } from './primitiveHelpers';
/**
 * Generates a globally identifiable CSS class for Hawkins components.
 */
export function createHawkinsComponentBaseClassName(componentName) {
    return "Hawkins-".concat(componentName);
}
/**
 * Generates a Hawkins-namespaced data attribute name.
 * The `namespace` parameter should generally be the name of the relevant hook or component.
 *
 * Note that all parameters will be kebab-cased in the final result.
 */
export function createHawkinsDataAttributeName(attributeId, _a) {
    var namespace = _a.namespace;
    return "data-hawkins-".concat(kebabCase(namespace), "-").concat(kebabCase(attributeId));
}
/**
 * Attempts to return the correct attributes for a disabled HTML element.
 * Note that it will only work if the `element` argument represents an HTML tag (rather than a custom React component).
 */
export function getDisabledElementProps(element, isDisabled, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.alwaysUseAria, alwaysUseAria = _c === void 0 ? false : _c;
    var result = {};
    if (isDisabled) {
        result[alwaysUseAria || !supportsDisabledAttribute(element) ? 'aria-disabled' : 'disabled'] = true;
    }
    return result;
}
/**
 * A function for taking a best guess as to a component's `href` value (if any).
 * Takes both `href` and `to` into account, as popular libraries like React Router use `props.to` to generate a link's `href` attribute.
 */
export function getHrefValue(_a) {
    var href = _a.href, to = _a.to;
    // Allow for a blank `href` string or even an object (for a custom link component).
    if (!isNil(href))
        return href;
    if (!isNil(to))
        return to;
    return null;
}
/**
 * Takes both `props.href` and `props.to` into account when determining a link's attributes.
 * Popular libraries like React Router use a `to` prop for links, so we want to support/be aware of it.
 */
export function getLinkProps(element, _a) {
    var href = _a.href, to = _a.to;
    var result = {};
    // Since `props.to` is intended to be used by a third-party library,
    // it can be anything (string, object, etc.), so only check that it's populated.
    if (!isNil(to)) {
        if (isString(element)) {
            // If no custom component has been specified, convert `props.to` to `props.href` so the Hawkins component can render a normal link.
            if (isString(to))
                result.href = to;
        }
        else {
            // If a custom component has been specified, pass `props.to` through in case it's used by the custom component.
            result.to = to;
        }
    }
    // Don't override `props.href` if the consumer has populated it.
    // An empty string is a valid `href` value, so we can't just check for falsiness.
    if (!isNil(href))
        result.href = href;
    return result;
}
export function hasARIAHiddenValue(props, value) {
    return props['aria-hidden'] === value || props['aria-hidden'] === String(value);
}
export function hasARIALabel(props) {
    return !!props['aria-label'] || !!props['aria-labelledby'];
}
export function isEditableElement(el) {
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el instanceof HTMLElement && !!el.isContentEditable);
}
/**
 * For narrowing the type of an `Element` queried from the DOM.
 */
export function isHTMLElement(el) {
    return el instanceof HTMLElement;
}
/**
 * Returns `true` if a scrollable element is scrolled to the bottom.
 */
export function isScrolledToBottom(_a) {
    var clientHeight = _a.clientHeight, scrollHeight = _a.scrollHeight, scrollTop = _a.scrollTop;
    // Account for decimal values if browser is zoomed in
    // (see https://stackoverflow.com/questions/5828275/how-to-check-if-a-div-is-scrolled-all-the-way-to-the-bottom-with-jquery/32283147#comment51586429_5828342).
    return Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
}
/**
 * Returns `true` if a scrollable element is scrolled to the top.
 */
export function isScrolledToTop(_a) {
    var scrollTop = _a.scrollTop;
    return scrollTop === 0;
}
/**
 * Returns `true` if any part of the given element is visible inside its scrollable parent element.
 * Note that the parent MUST be positioned (e.g., `position: relative`) for the calculation to work properly.
 */
export function isVisibleInScrollContainer(_a) {
    var container = _a.container, el = _a.el;
    if (!container.contains(el))
        throw new Error('The container does not contain the given element.');
    var clientHeight = container.clientHeight, scrollTop = container.scrollTop;
    var offsetHeight = el.offsetHeight, offsetTop = el.offsetTop;
    var offsetBottom = offsetTop + offsetHeight;
    var scrollBottom = scrollTop + clientHeight;
    return offsetTop <= scrollBottom && offsetBottom >= scrollTop;
}
/**
 * For certain HTML elements, the browser should usually handle tabbing/focus behavior
 * (i.e., we shouldn't set `tabindex` ourselves) for accessibility purposes.
 */
export function shouldBrowserHandleFocus(component) {
    return component === 'a' || component === 'button';
}
/**
 * Some HTML elements support a `disabled` attribute, so we should set `disabled` rather than `aria-disabled`.
 */
export function supportsDisabledAttribute(component) {
    // List taken from https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled.
    return (component === 'button' ||
        component === 'fieldset' ||
        component === 'input' ||
        component === 'keygen' ||
        component === 'optgroup' ||
        component === 'option' ||
        component === 'select' ||
        component === 'textarea');
}
/**
 * Quick check to see if can use DOM apis, useful to guard for SSR
 */
export function canUseDOM() {
    return !!(typeof window !== 'undefined' && window.document && window.document.createElement);
}
/**
 * Check if element target is child of an element with the id provided
 * via id params
 * @param e event
 * @param id id of parent element
 * @returns true if the child is a child of an element with the provided id
 */
export function isTargetChildOfElementId(e, id) {
    var child = e === null || e === void 0 ? void 0 : e.target;
    if (!id) {
        console.warn('Missing parent element ID. Unable to detect if target is a child of parent element. Please provide an unique id for each of your dropdown component');
        return false;
    }
    return Boolean(child === null || child === void 0 ? void 0 : child.closest("#".concat(id)));
}
/**
 * Given an event and an valid DOM query selector,
 * determines if the event target is contained within the the DOM tree of the selector
 */
export function isTargetChildOfSelector(e, selector) {
    var child = e.target;
    return Boolean(child.closest(selector));
}
export var HawkinsGlobalClass = {
    ACTIVE: 'Hawkins-active',
    FOCUS_RING: 'Hawkins-focusRing',
    FOCUSABLE: 'Hawkins-focusable',
    HIDDEN: 'Hawkins-hidden',
    HOVER: 'Hawkins-hover',
    ICON: 'Hawkins-Icon',
    ICON_SMALL: 'Hawkins-Icon-Small',
    SELECTED: 'Hawkins-selected',
    TRUNCATE: 'Hawkins-truncate',
};
//# sourceMappingURL=domHelpers.js.map